import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { PrivacyPolicy } from '../../lib/rgpd';
import type { SupportedLanguages } from '../../i18n';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  policy: PrivacyPolicy[];
  config: any;
  lastUpdate?: string;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  isOpen,
  onClose,
  policy,
  config,
  lastUpdate
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguages;

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatContent = (content: string): string => {
    if (lastUpdate && content.includes('{{last_update}}')) {
      const date = new Date(lastUpdate);
      const formattedDate = new Intl.DateTimeFormat(currentLanguage, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
      return content.replace(/{{last_update}}/g, formattedDate);
    }
    return content;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto relative"
        style={{ borderRadius: config.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10">
          <div className="flex justify-between items-center p-6">
            <h2 
              className="text-2xl font-bold"
              style={{ color: config['dark-color'] }}
            >
              {policy[0][`title_${currentLanguage}`] || policy[0].title_fr}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {policy.map((section) => (
            <section key={section.id} className="space-y-4">
              {section.key !== 'header' && (
                <h3 
                  className="text-xl font-semibold"
                  style={{ color: config['dark-color'] }}
                >
                  {section[`title_${currentLanguage}`] || section.title_fr}
                </h3>
              )}
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: formatContent(section[`content_${currentLanguage}`] || section.content_fr)
                }}
              />
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-white rounded-lg transition-colors"
            style={{ 
              backgroundColor: config['major-color'],
              borderRadius: config.border_radius 
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
